import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import {
  getGroupMembers,
  setGroupMembers,
  addGroupInclude,
  getGroupIncludes,
  listGroups,
  removeGroupInclude,
  resolveGroupEmails,
} from '../../../api/mailingApi';

const ASAHI_REGEX =
  /^[^\s@]+@(?:asahibeer\.pl|asahibeer\.com|eu\.asahibeer\.pl|eu\.asahibeer\.com)$/i;

const GroupDetailsPage = () => {
  const { t } = useTranslation();
  const { id: groupId } = useParams();

  const [loading, setLoading] = useState(false);
  const [group, setGroup] = useState(null);

  const [emails, setEmails] = useState([]); // edytowalna lista
  const [emailInput, setEmailInput] = useState('');

  const [snack, setSnack] = useState({
    open: false,
    severity: 'success',
    msg: '',
  });
  const [includesData, setIncludesData] = useState(null);

  const [includesLoading, setIncludesLoading] = useState(false);

  const [addIncludeOpen, setAddIncludeOpen] = useState(false);
  const [allGroups, setAllGroups] = useState([]);
  const [pickFilter, setPickFilter] = useState('');
  const [selectedChildId, setSelectedChildId] = useState('');
  const [resolveOnlyActive, setResolveOnlyActive] = useState(true);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolved, setResolved] = useState(null);

  async function loadResolve() {
    setResolveLoading(true);
    try {
      const data = await resolveGroupEmails(groupId, resolveOnlyActive);
      setResolved(data);
    } catch (e) {
      showError(e, 'Nie udało się wyliczyć resolve');
    } finally {
      setResolveLoading(false);
    }
  }

  const showError = (e, fallback) => {
    const msg = e?.response?.data?.message || e?.message || fallback || 'Błąd';
    setSnack({ open: true, severity: 'error', msg: String(msg) });
  };
  const showOk = (msg) => setSnack({ open: true, severity: 'success', msg });

  async function loadIncludes() {
    setIncludesLoading(true);
    try {
      const data = await getGroupIncludes(groupId);
      setIncludesData(data);
    } catch (e) {
      showError(e, 'Nie udało się wczytać includes');
    } finally {
      setIncludesLoading(false);
    }
  }

  async function loadAllGroupsForPicker() {
    try {
      // można tu przekazać filtry, np. tylko ten sam warehouseCode
      const data = await listGroups({});
      setAllGroups(Array.isArray(data) ? data : []);
    } catch (e) {
      showError(e, 'Nie udało się wczytać listy grup');
    }
  }

  async function load() {
    setLoading(true);
    try {
      const data = await getGroupMembers(groupId);
      setGroup(data);

      // members -> emails
      const loadedEmails = (data?.members || [])
        .map((m) => m?.recipient?.email)
        .filter(Boolean);

      // dedupe + sort
      const unique = [...new Set(loadedEmails)].sort();
      setEmails(unique);
    } catch (e) {
      showError(e, 'Nie udało się wczytać grupy');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    loadIncludes();
    loadResolve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const helper = useMemo(() => {
    if (!emailInput.trim()) return '';
    const normalized = emailInput.trim().toLowerCase();
    if (!ASAHI_REGEX.test(normalized))
      return 'Dozwolone domeny: asahibeer.pl / asahibeer.com';
    if (emails.includes(normalized)) return 'Ten email już jest na liście';
    return '';
  }, [emailInput, emails]);

  function addFromInput() {
    const v = emailInput.trim().toLowerCase();
    if (!v) return;
    if (!ASAHI_REGEX.test(v)) return;
    if (emails.includes(v)) return;

    setEmails((prev) => [...prev, v].sort());
    setEmailInput('');
  }

  function removeEmail(v) {
    setEmails((prev) => prev.filter((x) => x !== v));
  }

  async function onSave() {
    setLoading(true);
    try {
      await setGroupMembers(groupId, emails);
      showOk('Zapisano członków grupy');
      await load();
      await loadResolve();
    } catch (e) {
      showError(e, 'Nie udało się zapisać');
    } finally {
      setLoading(false);
    }
  }

  async function onOpenAddInclude() {
    setSelectedChildId('');
    setPickFilter('');
    setAddIncludeOpen(true);
    await loadAllGroupsForPicker();
  }

  async function onAddInclude() {
    if (!selectedChildId) return;

    setLoading(true);
    try {
      await addGroupInclude(groupId, selectedChildId);
      showOk('Dodano dołączoną grupę');
      setAddIncludeOpen(false);

      await loadIncludes();
      await loadResolve();
    } catch (e) {
      showError(e, 'Nie udało się dodać include');
    } finally {
      setLoading(false);
    }
  }

  async function onRemoveInclude(childGroupId) {
    const ok = window.confirm('Usunąć dołączoną grupę?');
    if (!ok) return;

    setLoading(true);
    try {
      await removeGroupInclude(groupId, childGroupId);
      showOk('Usunięto include');
      await loadIncludes();
      await loadResolve();
    } catch (e) {
      showError(e, 'Nie udało się usunąć include');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h5">{t('masterData.mailingDetail')}</Typography>

        <Paper sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle1">
              {group?.name || '(brak nazwy)'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Magazyn: {group?.warehouseCode || '-'} • Typ:{' '}
              {group?.messageType || '-'}
            </Typography>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">{t('masterData.setMembers')}</Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField
                fullWidth
                size="small"
                label="Dodaj email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addFromInput()}
                helperText={helper}
                error={Boolean(helper) && helper.includes('Dozwolone')}
              />
              <Button
                variant="contained"
                onClick={addFromInput}
                disabled={loading}
              >
                Dodaj
              </Button>
            </Stack>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {emails.map((e) => (
                <Chip key={e} label={e} onDelete={() => removeEmail(e)} />
              ))}
              {!emails.length ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Brak członków w grupie
                </Typography>
              ) : null}
            </Box>

            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={onSave} disabled={loading}>
                Zapisz
              </Button>
              <Button variant="outlined" onClick={load} disabled={loading}>
                Odrzuć zmiany
              </Button>
            </Stack>

            {loading ? (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                Ładowanie...
              </Alert>
            ) : null}
          </Stack>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Includes (child / parent)</Typography>
              <Button
                variant="contained"
                onClick={onOpenAddInclude}
                disabled={loading}
              >
                Dodaj dołączoną grupę
              </Button>
            </Stack>

            {includesLoading ? (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                Ładowanie includes...
              </Alert>
            ) : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {/* CHILD GROUPS */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1">
                  Dołączone grupy (child)
                </Typography>
                <Divider sx={{ my: 1 }} />

                <List dense>
                  {(includesData?.includes || []).map((x) => (
                    <ListItem
                      key={x.childGroupId}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => onRemoveInclude(x.childGroupId)}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={`${x.childGroup?.warehouseCode || '-'} • ${
                          x.childGroup?.messageType || '-'
                        }`}
                        secondary={x.childGroup?.name || '(brak nazwy)'}
                      />
                    </ListItem>
                  ))}

                  {!includesData?.includes ||
                  includesData.includes.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', p: 1 }}
                    >
                      Brak dołączonych grup
                    </Typography>
                  ) : null}
                </List>
              </Box>

              {/* PARENT GROUPS */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1">
                  Grupy nadrzędne (parent)
                </Typography>
                <Divider sx={{ my: 1 }} />

                <List dense>
                  {(includesData?.includedBy || []).map((x) => (
                    <ListItem key={x.parentGroupId}>
                      <ListItemText
                        primary={`${x.parentGroup?.warehouseCode || '-'} • ${
                          x.parentGroup?.messageType || '-'
                        }`}
                        secondary={x.parentGroup?.name || '(brak nazwy)'}
                      />
                    </ListItem>
                  ))}

                  {!includesData?.includedBy ||
                  includesData.includedBy.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', p: 1 }}
                    >
                      Brak grup nadrzędnych
                    </Typography>
                  ) : null}
                </List>
              </Box>
            </Stack>
          </Stack>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems="center"
            >
              <Typography variant="h6" sx={{ flex: 1 }}>
                Resolve (finalna lista wysyłki)
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={resolveOnlyActive}
                    onChange={(e) => setResolveOnlyActive(e.target.checked)}
                  />
                }
                label="Tylko aktywne"
              />

              <Button
                variant="contained"
                onClick={loadResolve}
                disabled={loading || resolveLoading}
              >
                Odśwież
              </Button>
            </Stack>

            {resolveLoading ? (
              <Alert severity="info" icon={<CircularProgress size={16} />}>
                Liczenie resolve...
              </Alert>
            ) : null}

            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Emaili: <b>{resolved?.count ?? 0}</b>
            </Typography>

            <Paper
              variant="outlined"
              sx={{ p: 1, maxHeight: 260, overflow: 'auto' }}
            >
              <Stack spacing={0.5}>
                {(resolved?.emails || []).map((e) => (
                  <Typography key={e} variant="body2">
                    {e}
                  </Typography>
                ))}

                {!resolved?.emails || resolved.emails.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Brak emaili do wysyłki
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          </Stack>
        </Paper>
      </Stack>
      <Dialog
        open={addIncludeOpen}
        onClose={() => setAddIncludeOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Dodaj dołączoną grupę</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Wybierz grupę, którą chcesz dołączyć jako <b>child</b>. System
              zablokuje cykle.
            </Alert>

            <TextField
              label="Filtruj (warehouse / type / name)"
              value={pickFilter}
              onChange={(e) => setPickFilter(e.target.value)}
              fullWidth
              size="small"
            />

            <TextField
              select
              SelectProps={{ native: true }}
              label="Grupa do dołączenia"
              value={selectedChildId}
              onChange={(e) => setSelectedChildId(e.target.value)}
              fullWidth
              size="small"
            >
              <option value="">-- wybierz --</option>
              {allGroups
                .filter((g) => g.id !== groupId) // nie można dołączyć samej siebie
                .filter((g) => {
                  const f = pickFilter.trim().toLowerCase();
                  if (!f) return true;
                  const text = `${g.warehouseCode} ${g.messageType} ${
                    g.name || ''
                  }`.toLowerCase();
                  return text.includes(f);
                })
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.warehouseCode} • {g.messageType} •{' '}
                    {g.name || '(brak nazwy)'}
                  </option>
                ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddIncludeOpen(false)} disabled={loading}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            onClick={onAddInclude}
            disabled={loading || !selectedChildId}
          >
            Dodaj
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupDetailsPage;
